import React, {useEffect, useMemo, useState} from "react";
import useApiStable from "../../utils/useApiStable";

const FORMAT_OPTIONS = [
    {value: "ZWEI_ER", label: "2er Staffel (2 Einzel, 1 Doppel)"},
    {value: "VIER_ER", label: "4er Staffel (4 Einzel, 2 Doppel)"},
    {value: "SECHS_ER", label: "6er Staffel (6 Einzel, 3 Doppel)"},
];

const getNormalizedRank = (rank) => {
    if (rank === null || rank === undefined || rank === "") {
        return Number.MAX_SAFE_INTEGER;
    }

    const normalized = Number(rank);
    return Number.isFinite(normalized) ? normalized : Number.MAX_SAFE_INTEGER;
};

const LeaguePlayerManager = ({team, canManage, isAdmin, canSeeMembers, onTeamChange}) => {
    const api = useApiStable();
    const [allPersons, setAllPersons] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [licensedPlayers, setLicensedPlayers] = useState([]);
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncError, setSyncError] = useState(null);
    const [formatSaving, setFormatSaving] = useState(false);

    useEffect(() => {
        if (canManage) {
            api.get("/persons")
                .then(setAllPersons)
                .catch(err => setError(err.message));
        }
        api.get(`/league/teams/${team.id}/licensed-players`)
            .then(setLicensedPlayers)
            .catch(() => {
            });
    }, [canManage, team.id]);

    const handleSyncPlayers = async () => {
        setSyncLoading(true);
        setSyncError(null);
        try {
            const players = await api.post(`/league/teams/${team.id}/sync-players`);
            setLicensedPlayers(players || []);
        } catch (err) {
            setSyncError(err.message || "Synchronisierung fehlgeschlagen");
        } finally {
            setSyncLoading(false);
        }
    };

    const handleSetFormat = async (format) => {
        setFormatSaving(true);
        try {
            await api.put(`/league/teams/${team.id}/match-format`, {format});
            onTeamChange();
        } catch (err) {
            setError(err.message);
        } finally {
            setFormatSaving(false);
        }
    };

    const handleRemove = async (personUuid) => {
        setLoading(true);
        try {
            await api.delete(`/league/teams/${team.id}/players/${personUuid}`);
            onTeamChange();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSetCaptain = async (personUuid) => {
        setLoading(true);
        try {
            await api.put(`/league/teams/${team.id}/captain/${personUuid}`);
            onTeamChange();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClearCaptain = async () => {
        setLoading(true);
        try {
            await api.delete(`/league/teams/${team.id}/captain`);
            onTeamChange();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async (personUuid) => {
        setLoading(true);
        try {
            await api.post(`/league/teams/${team.id}/players/${personUuid}`);
            onTeamChange();
            setSearchQuery("");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const existingUuids = new Set(team.players.map(p => p.uuid));
    const sortedLicensedPlayers = useMemo(
        () => [...licensedPlayers].sort((left, right) => {
            const rankDifference = getNormalizedRank(left.rank) - getNormalizedRank(right.rank);
            if (rankDifference !== 0) {
                return rankDifference;
            }

            const nameCompare = (left.name || "").localeCompare(right.name || "", "de", {sensitivity: "base"});
            if (nameCompare !== 0) {
                return nameCompare;
            }

            return (left.licenseNumber || "").localeCompare(right.licenseNumber || "", "de", {sensitivity: "base"});
        }),
        [licensedPlayers]
    );
    const searchResults = canManage && searchQuery.length >= 2
        ? allPersons.filter(p =>
            !existingUuids.has(p.uuid) &&
            (p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.email?.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        : [];

    return (
        <div>
            {error && <p style={{color: "red", marginBottom: 12}}>{error}</p>}

            {/* Staffelformat */}
            {canManage && (
                <div style={{
                    marginBottom: 20,
                    padding: "10px 14px",
                    background: "#f8f9fa",
                    borderRadius: 6,
                    border: "1px solid #e0e0e0"
                }}>
                    <div style={{fontWeight: "bold", fontSize: 13, marginBottom: 8}}>Staffelformat</div>
                    <select
                        value={team.matchFormat || "SECHS_ER"}
                        onChange={e => handleSetFormat(e.target.value)}
                        disabled={formatSaving}
                        style={{padding: "6px 10px", fontSize: 13, border: "1px solid #ddd", borderRadius: 4}}
                    >
                        {FORMAT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Mannschaftsspieler – nur für Mitglieder und Admins */}
            {canSeeMembers && (
                <div style={{marginBottom: 20}}>
                    <h3 style={{fontSize: 15, marginBottom: 12}}>
                        Spieler ({team.players.length})
                    </h3>

                    {team.players.length === 0 ? (
                        <p style={{color: "#666", fontStyle: "italic"}}>Noch keine Spieler zugeordnet.</p>
                    ) : (
                        <div style={{display: "flex", flexDirection: "column", gap: 6, marginBottom: 16}}>
                            {team.players.map(player => (
                                <div
                                    key={player.uuid}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "8px 12px",
                                        border: "1px solid #ddd",
                                        borderRadius: 4,
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    <div>
                                        <div style={{fontWeight: "bold", fontSize: 14}}>{player.name}</div>
                                        <div style={{fontSize: 12, color: "#888"}}>{player.email}</div>
                                    </div>
                                    <div style={{display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap"}}>
                                        {team.captain?.uuid === player.uuid && (
                                            <>
                                                <span className="groupphase-status-badge groupphase-status-badge--success"
                                                      style={{fontSize: 11}}>
                                                    Mannschaftsführer
                                                </span>
                                                {isAdmin && (
                                                    <button
                                                        onClick={handleClearCaptain}
                                                        disabled={loading}
                                                        title="Mannschaftsführer zurücksetzen"
                                                        style={{
                                                            padding: "3px 10px",
                                                            fontSize: 12,
                                                            cursor: "pointer",
                                                            color: "#888",
                                                            border: "1px solid #ccc",
                                                            background: "#fff",
                                                            borderRadius: 4,
                                                        }}
                                                    >
                                                        Zurücksetzen
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        {isAdmin && team.captain?.uuid !== player.uuid && (
                                            <button
                                                onClick={() => handleSetCaptain(player.uuid)}
                                                disabled={loading}
                                                style={{
                                                    padding: "3px 10px",
                                                    fontSize: 12,
                                                    cursor: "pointer",
                                                    color: "#1565c0",
                                                    border: "1px solid #1565c0",
                                                    background: "#fff",
                                                    borderRadius: 4,
                                                }}
                                            >
                                                Als Mannschaftsführer:in setzen
                                            </button>
                                        )}
                                        {canManage && (
                                            <button
                                                onClick={() => handleRemove(player.uuid)}
                                                disabled={loading}
                                                style={{
                                                    padding: "3px 10px",
                                                    fontSize: 12,
                                                    cursor: "pointer",
                                                    color: "#e65100",
                                                    border: "1px solid #e65100",
                                                    background: "#fff",
                                                    borderRadius: 4,
                                                }}
                                            >
                                                Entfernen
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {canManage && (
                        <div style={{marginTop: 8}}>
                            <h4 style={{fontSize: 14, marginBottom: 8}}>Spieler hinzufügen</h4>
                            <input
                                type="text"
                                placeholder="Name oder E-Mail suchen..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "8px 12px",
                                    border: "1px solid #ddd",
                                    borderRadius: 4,
                                    fontSize: 14,
                                    boxSizing: "border-box",
                                }}
                            />
                            {searchResults.length > 0 && (
                                <div style={{
                                    border: "1px solid #ddd",
                                    borderTop: "none",
                                    borderRadius: "0 0 4px 4px",
                                    backgroundColor: "#fff",
                                    maxHeight: 200,
                                    overflowY: "auto",
                                }}>
                                    {searchResults.map(person => (
                                        <div
                                            key={person.uuid}
                                            onClick={() => handleAdd(person.uuid)}
                                            style={{
                                                padding: "8px 12px",
                                                cursor: "pointer",
                                                borderBottom: "1px solid #eee",
                                                fontSize: 14,
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f5f5f5"}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = "#fff"}
                                        >
                                            <strong>{person.name}</strong>
                                            <span style={{color: "#888", marginLeft: 8, fontSize: 12}}>{person.email}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {searchQuery.length >= 2 && searchResults.length === 0 && (
                                <div style={{padding: "8px 12px", color: "#888", fontSize: 13}}>
                                    Keine Personen gefunden.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Meldeliste – öffentlich sichtbar */}
            <div style={{marginBottom: 20}}>
                <div style={{display: "flex", alignItems: "center", gap: 12, marginBottom: 8}}>
                    <div style={{fontWeight: "bold", fontSize: 14}}>
                        Meldeliste ({licensedPlayers.length})
                    </div>
                    {canManage && (
                        <button
                            onClick={handleSyncPlayers}
                            disabled={syncLoading}
                            style={{
                                padding: "4px 12px",
                                fontSize: 12,
                                background: "#e3f2fd",
                                color: "#1565c0",
                                border: "1px solid #1565c0",
                                borderRadius: 4,
                                cursor: "pointer",
                            }}
                        >
                            {syncLoading ? "Synchronisiere..." : "Von TNB synchronisieren"}
                        </button>
                    )}
                </div>
                {syncError && <p style={{color: "#c62828", fontSize: 12, marginBottom: 8}}>{syncError}</p>}
                {licensedPlayers.length === 0 ? (
                    <p style={{color: "#888", fontSize: 13, fontStyle: "italic"}}>
                        Noch keine Meldeliste synchronisiert.
                        {canManage && " Klicke auf \"Von TNB synchronisieren\"."}
                    </p>
                ) : (
                    <div style={{display: "flex", flexDirection: "column", gap: 4}}>
                        {sortedLicensedPlayers.map(p => (
                            <div
                                key={p.licenseNumber || p.idNumber || p.name}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                    fontSize: 13,
                                    padding: "8px 0",
                                    borderBottom: "1px solid #f0f0f0",
                                }}
                            >
                                <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    flexWrap: "wrap"
                                }}>
                                    <span style={{fontWeight: "bold"}}>{p.name}</span>
                                    <span style={{color: "#666"}}>Rang: {p.rank ?? "—"}</span>
                                </div>
                                <div style={{display: "flex", gap: 12, flexWrap: "wrap", color: "#666"}}>
                                    <span>LK: {p.lk || "—"}</span>
                                    <span>ID-Nr.: {p.idNumber || "—"}</span>
                                    <span>Lizenz-Nr.: {p.licenseNumber || "—"}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeaguePlayerManager;
